/**
 * @file SidebarMenuItem.tsx
 * @module _admin/layout/components/sidebar/sidebar-menu/SidebarMenuItem
 * @description Reusable sidebar navigation item for single-link entries in the
 * UTrade admin layout. Handles route active-state styling, optional SVG/font
 * icons, bullet display, notification counters, and root-level custom class
 * injection for guided walkthrough or contextual highlighting.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { FC } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { checkIsActive, KTSVG, WithChildren } from '../../../../helpers';
import { useLayout } from '../../../core';
import DefaultMenuIconSVG from '../../../../assets/media/dashboard/master.svg';
type Props = {
  to: string;
  title: string;
  icon?: string;
  fontIcon?: string;
  hasBullet?: boolean;
  hasCount?: boolean;
  counter?: number;
  activeMatch?: string;
  /** Marks the item active for any path that starts with this prefix. */
  activePrefix?: string;
  className?: string;
};
const SidebarMenuItem: FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon = DefaultMenuIconSVG,
  fontIcon,
  hasBullet = false,
  hasCount = false,
  counter,
  activeMatch,
  activePrefix,
  className,
}) => {
  const { pathname } = useLocation();
  const isActive =
    checkIsActive(pathname, to) ||
    (activeMatch ? checkIsActive(pathname, activeMatch) : false) ||
    (activePrefix ? pathname.startsWith(activePrefix) : false);
  const { config } = useLayout();
  const { app } = config;
  return (
    <div className={clsx('menu-item', className)}>
      <Link
        className={clsx('menu-link without-sub', { active: isActive })}
        to={to}
      >
        {hasBullet && (
          <span className="menu-bullet">
            <span className="bullet bullet-dot"></span>
          </span>
        )}
        {icon && app?.sidebar?.default?.menu?.iconType === 'svg' && (
          <span className="menu-icon">
            <KTSVG path={icon} className="svg-icon-2" />
          </span>
        )}
        {fontIcon && app?.sidebar?.default?.menu?.iconType === 'font' && (
          <i className={clsx('bi fs-3', fontIcon)}></i>
        )}
        <span className="menu-title">{title}</span>
        {/* Notification Number */}
        {hasCount && counter && (
          <div className="px-2 border-r5px min-w-25px text-center bg-primary">
            <span className="fs-12 fw-600 text-white">{counter}</span>
          </div>
        )}
      </Link>
      {children}
    </div>
  );
};
export { SidebarMenuItem };
